import React, {
    RefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import scrollIntoView from 'scroll-into-view-if-needed';
import {FrameRate} from '@accurate-player/accurate-player-core';
import {OnMouseButtonEvent, OnStringSetState} from '@pluto/@types';
import {TimespanCategory} from '@pluto/rest';
import {QCReportMarker} from 'VideoAudioQC/Timeline/TimelineTypes';
import List, {QCNoteData} from './List';

export type OnEditQCReportMarkerProps = {
    marker: QCReportMarker,
    inpoint?: boolean,
};
export type OnEditMarker = (props: OnEditQCReportMarkerProps) => void;
export type OnButtonClick = (data: QCReportMarker) => void;
export type OnRowClick = (item: any, column: string) => void;
export type OnAddRemoveTimecodes = (data: QCNoteData, column: string, add: boolean, event: OnMouseButtonEvent) => void;
export type OnMoveTime = (data: QCNoteData, column: string, event: OnMouseButtonEvent) => void;
export type ListItemRef = RefObject<HTMLDivElement | null>;

export interface ListContainerProps {
    markers: QCReportMarker[];
    noteTypes: TimespanCategory[];
    selectedMarkerId: string;
    frameRate: FrameRate;
    dropFrame: boolean;
    duration: number;
    onMarkerClick: (marker: QCNoteData) => void;
    onGoToStartClick: OnButtonClick;
    onGoToEndClick: OnButtonClick;
    onEditMarkerIn: OnButtonClick;
    onEditMarkerOut: OnButtonClick;
    onDeleteClick: OnButtonClick;
    onEditMarker: OnEditMarker;
    jobDeliveryDate: string;
}

export interface OnRowClickProps {
    onMarkerClick: (marker: QCNoteData) => void;
    setSelectedColumn: OnStringSetState;
}

export const onRowClick = curry((props: OnRowClickProps, item: QCNoteData, column: string) => {
    const {onMarkerClick, setSelectedColumn} = props;
    
    onMarkerClick(item);
    setSelectedColumn(column);
});

export const onButtonClick = curry((click: OnButtonClick, data: QCReportMarker) => {
    click(data);
});

export type OnAddRemoveTimecodesProps = {
    setSelectedColumn: OnStringSetState,
    onEditMarker: OnEditMarker,
};

export const onAddRemoveTimecodes = curry((props: OnAddRemoveTimecodesProps, data: QCNoteData, column: string, add: boolean, event: OnMouseButtonEvent) => {
    const {
        setSelectedColumn,
        onEditMarker,
    } = props;
    
    event.stopPropagation();
    event.preventDefault();
    
    const start = add ? 0 : undefined;
    const end = add ? 1 : undefined;
    
    const marker: QCReportMarker = {
        ...data,
        start,
        end,
    };
    
    onEditMarker({
        marker,
    });
    setSelectedColumn(column);
});

export type OnMoveTimeProps = {
    setSelectedColumn: OnStringSetState,
    onEditMarkerInOut: OnButtonClick,
};

export const onMoveTime = curry((props: OnMoveTimeProps, data: QCNoteData, column: string, event: OnMouseButtonEvent) => {
    const {
        setSelectedColumn,
        onEditMarkerInOut,
    } = props;
    
    event.stopPropagation();
    event.preventDefault();
    
    onEditMarkerInOut(data);
    setSelectedColumn(column);
});

export const onItemScroll = wrap((listItemRef: ListItemRef) => {
    const {current} = listItemRef;
    
    if (!current)
        return;
    
    scrollIntoView(current, {
        block: 'center',
        scrollMode: 'if-needed',
    });
});

export default function ListContainer(props: ListContainerProps) {
    const {
        markers,
        noteTypes,
        selectedMarkerId,
        frameRate,
        dropFrame,
        duration,
        onMarkerClick,
        onDeleteClick,
        onEditMarker,
        onEditMarkerIn,
        onEditMarkerOut,
        onGoToStartClick,
        onGoToEndClick,
        jobDeliveryDate,
    } = props;
    const [selectedColumn, setSelectedColumn] = useState('');
    const onRowClickProps: OnRowClickProps = {
        onMarkerClick,
        setSelectedColumn,
    };
    const handleRowClick = onRowClick(onRowClickProps);
    const handleDeleteClick = onButtonClick(onDeleteClick);
    const handleAddRemoveTimecodes = onAddRemoveTimecodes({
        onEditMarker,
        setSelectedColumn,
    });
    const handleMoveStartTime = onMoveTime({
        onEditMarkerInOut: onEditMarkerIn,
        setSelectedColumn,
    });
    const handleMoveEndTime = onMoveTime({
        onEditMarkerInOut: onEditMarkerOut,
        setSelectedColumn,
    });
    
    const selectedItemRef = useRef<HTMLDivElement | null>(null);
    const handleItemScroll = onItemScroll(selectedItemRef);
    
    useEffect(handleItemScroll, [selectedColumn, markers]);
    
    return (
        <List
            jobDeliveryDate={jobDeliveryDate}
            markers={markers}
            noteTypes={noteTypes}
            selectedMarkerId={selectedMarkerId}
            frameRate={frameRate}
            dropFrame={dropFrame}
            duration={duration}
            selectedColumn={selectedColumn}
            selectedItemRef={selectedItemRef}
            onRowClick={handleRowClick}
            onDeleteClick={handleDeleteClick}
            onEditMarker={onEditMarker}
            onAddRemoveTimecodes={handleAddRemoveTimecodes}
            onEditMarkerIn={handleMoveStartTime}
            onEditMarkerOut={handleMoveEndTime}
            onGoToStartClick={onGoToStartClick}
            onGoToEndClick={onGoToEndClick}
        />
    );
}
